/**
 * Trovva — Dashboard Trend Charts
 *
 * Pure SVG charts — no external dependency. Renders:
 *   1. Pipeline Value (area chart, 6 months)
 *   2. Deals Closed (bar chart, 6 months)
 *   3. Commission Earned (line chart, 6 months)
 *   4. Active Listings (bar chart, 6 months)
 *   5. Avg Days to Close (area chart, 6 months)
 *   6. Lead Conversion Funnel (horizontal bar)
 *
 * All charts use theme tokens and adapt to light/dark mode.
 * In demo mode, renders realistic sample data.
 * In live mode, fetches from Supabase deals + contacts tables.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TrendingUp, BarChart3, DollarSign, Filter, RefreshCw,
  Loader2, ChevronDown, Home, Clock,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

// ── Demo data (6 months trailing) ──────────────────────────────

function getMonthLabels(count = 6) {
  const labels = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(d.toLocaleString('default', { month: 'short' }));
  }
  return labels;
}

const MONTHS = getMonthLabels(6);

const DEMO_DATA = {
  pipeline: [2800000, 3100000, 2600000, 3400000, 4100000, 5310000],
  dealsClosed: [3, 2, 4, 3, 5, 2],
  commission: [28000, 22000, 38000, 31000, 52000, 18400],
  activeListings: [8, 10, 7, 12, 14, 11],
  avgDaysToClose: [62, 55, 48, 51, 44, 39],
  funnel: {
    newLeads: 42,
    contacted: 35,
    qualified: 18,
    showings: 12,
    underContract: 6,
    closed: 2,
  },
};

// ── SVG Chart Primitives ───────────────────────────────────────

function AreaChart({ data, labels, color, height = 140, formatValue }) {
  const W = 100;
  const H = 100;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * W,
    y: H - ((v - min) / range) * (H * 0.85) - H * 0.05,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L${W},${H} L0,${H} Z`;

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`-5 -5 ${W + 10} ${H + 25}`} style={{ width: '100%', height }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={0} y1={H - pct * H * 0.85 - H * 0.05} x2={W} y2={H - pct * H * 0.85 - H * 0.05}
            stroke={theme.colors.border} strokeWidth={0.3} strokeDasharray="2,2" />
        ))}
        {/* Area fill */}
        <path d={areaD} fill={`${color}15`} />
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={color} stroke="white" strokeWidth={1} />
        ))}
        {/* X labels */}
        {labels.map((label, i) => (
          <text key={i} x={(i / (labels.length - 1)) * W} y={H + 14}
            textAnchor="middle" fontSize={5} fill={theme.colors.textLight} fontFamily="inherit">
            {label}
          </text>
        ))}
      </svg>
      {/* Value labels on hover — simplified: show last value */}
      <div style={{
        position: 'absolute', top: 4, right: 4, fontSize: 16, fontWeight: 800,
        color, lineHeight: 1,
      }}>
        {formatValue ? formatValue(data[data.length - 1]) : data[data.length - 1]}
      </div>
    </div>
  );
}

function BarChart({ data, labels, color, height = 140, formatValue }) {
  const max = Math.max(...data, 1);
  const barCount = data.length;
  const W = 100;
  const H = 100;
  const barW = W / (barCount * 1.8);
  const gap = barW * 0.8;

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`-5 -5 ${W + 10} ${H + 25}`} style={{ width: '100%', height }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={0} y1={H - pct * H * 0.9} x2={W} y2={H - pct * H * 0.9}
            stroke={theme.colors.border} strokeWidth={0.3} strokeDasharray="2,2" />
        ))}
        {/* Bars */}
        {data.map((v, i) => {
          const barH = (v / max) * H * 0.85;
          const x = (i / barCount) * W + gap;
          return (
            <g key={i}>
              <rect x={x} y={H - barH} width={barW} height={barH}
                rx={2} fill={color} opacity={0.85} />
              <text x={x + barW / 2} y={H - barH - 3}
                textAnchor="middle" fontSize={5} fontWeight="700" fill={color}>
                {v}
              </text>
            </g>
          );
        })}
        {/* X labels */}
        {labels.map((label, i) => (
          <text key={i} x={(i / barCount) * W + gap + barW / 2} y={H + 14}
            textAnchor="middle" fontSize={5} fill={theme.colors.textLight} fontFamily="inherit">
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function FunnelChart({ data, height = 160 }) {
  const steps = [
    { key: 'newLeads', label: 'New Leads', color: theme.colors.info },
    { key: 'contacted', label: 'Contacted', color: theme.colors.primary },
    { key: 'qualified', label: 'Qualified', color: theme.colors.primaryLight },
    { key: 'showings', label: 'Showings', color: theme.colors.accent },
    { key: 'underContract', label: 'Under Contract', color: theme.colors.warning },
    { key: 'closed', label: 'Closed', color: theme.colors.success },
  ];

  const maxVal = Math.max(...steps.map(s => data[s.key] || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {steps.map((step, i) => {
        const val = data[step.key] || 0;
        const pct = (val / maxVal) * 100;
        const convRate = i > 0 ? Math.round((val / (data[steps[i - 1].key] || 1)) * 100) : null;

        return (
          <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 100, fontSize: 11, fontWeight: 600, color: theme.colors.text, textAlign: 'right', flexShrink: 0 }}>
              {step.label}
            </div>
            <div style={{ flex: 1, position: 'relative', height: 22 }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, height: '100%',
                width: `${Math.max(pct, 4)}%`, borderRadius: 6,
                background: step.color, opacity: 0.2,
              }} />
              <div style={{
                position: 'absolute', top: 0, left: 0, height: '100%',
                width: `${Math.max(pct, 4)}%`, borderRadius: 6,
                background: `linear-gradient(90deg, ${step.color}40, ${step.color}80)`,
              }} />
              <span style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                fontSize: 11, fontWeight: 700, color: theme.colors.text,
              }}>
                {val}
              </span>
            </div>
            {convRate !== null && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: convRate >= 50 ? theme.colors.success : convRate >= 25 ? theme.colors.warning : theme.colors.danger,
                minWidth: 36, textAlign: 'right',
              }}>
                {convRate}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Chart Card wrapper ─────────────────────────────────────────

function ChartCard({ title, Icon, iconColor, children }) {
  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Icon size={13} color={iconColor || theme.colors.primary} />
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text }}>{title}</span>
      </div>
      <div style={{ padding: '12px 14px' }}>
        {children}
      </div>
    </div>
  );
}

// ── Formatting helpers ─────────────────────────────────────────

function fmtCurrency(val) {
  if (!val) return '$0';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

// ── Main Component ─────────────────────────────────────────────

export default function TrendCharts({ demoMode }) {
  const [data, setData] = useState(DEMO_DATA);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('6m');

  const fetchTrendData = useCallback(async () => {
    if (demoMode) {
      setData(DEMO_DATA);
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const now = new Date();
      const monthsBack = period === '3m' ? 3 : period === '12m' ? 12 : 6;
      const startDate = new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 1).toISOString();

      // Fetch deals
      const { data: deals } = await supabase
        .from('deals')
        .select('status, contract_price, close_date, commission_total, created_at')
        .gte('created_at', startDate);

      // Fetch contacts/leads
      const { data: contacts } = await supabase
        .from('contacts')
        .select('lead_status, created_at')
        .gte('created_at', startDate);

      if (!deals && !contacts) {
        setData(DEMO_DATA);
        return;
      }

      // Build monthly buckets
      const months = getMonthLabels(monthsBack);
      const pipeline = new Array(monthsBack).fill(0);
      const dealsClosed = new Array(monthsBack).fill(0);
      const commission = new Array(monthsBack).fill(0);
      const activeListings = new Array(monthsBack).fill(0);
      const daysToCloseByMonth = Array.from({ length: monthsBack }, () => []);

      (deals || []).forEach(deal => {
        const d = new Date(deal.created_at);
        const monthIdx = monthsBack - 1 - (now.getMonth() - d.getMonth() + (now.getFullYear() - d.getFullYear()) * 12);
        if (monthIdx >= 0 && monthIdx < monthsBack) {
          pipeline[monthIdx] += deal.contract_price || 0;
          // Count active listings (not closed, not cancelled)
          if (!['closed', 'fell_through', 'cancelled'].includes(deal.status)) {
            activeListings[monthIdx]++;
          }
          if (deal.status === 'closed') {
            dealsClosed[monthIdx]++;
            commission[monthIdx] += deal.commission_total || 0;
            // Calculate days to close
            if (deal.close_date && deal.created_at) {
              const days = Math.round((new Date(deal.close_date) - new Date(deal.created_at)) / (1000 * 60 * 60 * 24));
              if (days > 0 && days < 365) daysToCloseByMonth[monthIdx].push(days);
            }
          }
        }
      });

      // Average days to close per month (default 0 if no closings)
      const avgDaysToClose = daysToCloseByMonth.map(arr =>
        arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0
      );

      // Running pipeline total (cumulative active)
      const runningPipeline = pipeline.map((_, i) => {
        return pipeline.slice(0, i + 1).reduce((a, b) => a + b, 0);
      });

      // Build funnel from contacts
      const allContacts = contacts || [];
      const funnel = {
        newLeads: allContacts.length,
        contacted: allContacts.filter(c => !['new', 'uncontacted'].includes(c.lead_status)).length,
        qualified: allContacts.filter(c => ['qualified', 'hot', 'active', 'showing', 'under_contract', 'closed'].includes(c.lead_status)).length,
        showings: allContacts.filter(c => ['showing', 'under_contract', 'closed'].includes(c.lead_status)).length,
        underContract: allContacts.filter(c => ['under_contract', 'closed'].includes(c.lead_status)).length,
        closed: allContacts.filter(c => c.lead_status === 'closed').length,
      };

      setData({
        pipeline: runningPipeline,
        dealsClosed,
        commission,
        activeListings,
        avgDaysToClose,
        funnel,
      });
    } catch (e) {
      console.error('[TrendCharts] Fetch error:', e.message);
      setData(DEMO_DATA);
    } finally {
      setLoading(false);
    }
  }, [demoMode, period]);

  useEffect(() => { fetchTrendData(); }, [fetchTrendData]);

  const monthLabels = useMemo(() => getMonthLabels(period === '3m' ? 3 : period === '12m' ? 12 : 6), [period]);

  return (
    <div style={{ marginBottom: 18 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrendingUp size={14} color={theme.colors.primary} />
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Performance Trends</span>
          {loading && <Loader2 size={12} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['3m', '6m', '12m'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                border: `1px solid ${period === p ? theme.colors.primary : theme.colors.border}`,
                background: period === p ? `${theme.colors.primary}10` : theme.colors.card,
                color: period === p ? theme.colors.primary : theme.colors.textLight,
                cursor: 'pointer',
              }}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Charts grid — 2×2 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 14,
      }}>
        <ChartCard title="Pipeline Value" Icon={BarChart3} iconColor={theme.colors.primary}>
          <AreaChart
            data={data.pipeline}
            labels={monthLabels}
            color={theme.colors.primary}
            formatValue={fmtCurrency}
          />
        </ChartCard>

        <ChartCard title="Deals Closed" Icon={TrendingUp} iconColor={theme.colors.success}>
          <BarChart
            data={data.dealsClosed}
            labels={monthLabels}
            color={theme.colors.success}
          />
        </ChartCard>

        <ChartCard title="Commission Earned" Icon={DollarSign} iconColor={theme.colors.accent}>
          <AreaChart
            data={data.commission}
            labels={monthLabels}
            color={theme.colors.accent}
            formatValue={fmtCurrency}
          />
        </ChartCard>

        <ChartCard title="Active Listings" Icon={Home} iconColor="#8B5CF6">
          <BarChart
            data={data.activeListings}
            labels={monthLabels}
            color="#8B5CF6"
          />
        </ChartCard>

        <ChartCard title="Avg Days to Close" Icon={Clock} iconColor="#F59E0B">
          <AreaChart
            data={data.avgDaysToClose}
            labels={monthLabels}
            color="#F59E0B"
            formatValue={(v) => v ? `${v}d` : '—'}
          />
        </ChartCard>

        <ChartCard title="Lead Conversion Funnel" Icon={Filter} iconColor={theme.colors.info}>
          <FunnelChart data={data.funnel} />
        </ChartCard>
      </div>
    </div>
  );
}
